<template>
  <el-dialog
    :key="dialogKey"
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    :width="isView ? '900px' : '1000px'"
    top="5vh"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <!-- 价盘信息（始终显示） -->
    <div v-if="priceBook" class="book-info">
      <i class="el-icon-document book-info-icon"></i>
      <el-tag size="small" type="info">{{ projectTypeText(priceBook.projectType) }}</el-tag>
      <el-tag v-if="priceBook.isFallback === 'YES'" size="small" type="warning">兜底盘</el-tag>
      <span class="book-name">{{ priceBook.name }}</span>
    </div>

    <!-- 查看模式：批次维度只读展示 -->
    <template v-if="isView && taskRow">
      <div class="info-card-row">
        <el-card shadow="never" class="info-card">
          <div slot="header" class="info-card-header">
            <i class="el-icon-document info-card-icon"></i>
            <span>任务信息</span>
          </div>
          <el-descriptions :column="1" size="small" class="info-desc">
            <el-descriptions-item label="任务标题">
              <span v-if="isCancelBatch" style="display: inline-flex; align-items: center;">
                <el-tag type="danger" size="mini" style="margin-right: 6px">作废申请</el-tag>
                <span>{{ taskRow.title || '-' }}</span>
              </span>
              <span v-else>{{ taskRow.title || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="定价方式">
              <span v-if="isCancelBatch" style="color: #909399">-</span>
              <span v-else>{{ taskRow.adjustTypeName || taskRow.adjustType }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="调价参数">
              <span v-if="isCancelBatch" style="color: #909399">-</span>
              <span v-else-if="taskRow.adjustType === 'FORMULA'">{{ taskRow.formula || '-' }}</span>
              <span v-else>{{ taskRow.adjustValue != null && taskRow.adjustValue !== '' ? taskRow.adjustValue : '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <el-card shadow="never" class="info-card">
          <div slot="header" class="info-card-header">
            <i class="el-icon-time info-card-icon"></i>
            <span>状态与有效期</span>
          </div>
          <el-descriptions :column="1" size="small" class="info-desc">
            <el-descriptions-item label="生效时间">
              <span v-if="isCancelBatch" style="color: #909399">-</span>
              <span v-else>{{ displayTime(taskRow.effectiveStartTime) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="失效时间">
              <span v-if="isCancelBatch" style="color: #909399">-</span>
              <span v-else>{{ displayTime(taskRow.effectiveEndTime) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="审批状态">
              <el-tag :type="approvalStatusType(taskRow.approvalStatus)" size="mini">
                {{ approvalStatusText(taskRow.approvalStatus) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="展示状态">
              <el-tag :type="displayStatusType(taskRow.displayStatus)" size="mini">
                {{ taskRow.displayStatus || '-' }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </div>

      <el-card shadow="never" class="info-card info-card-full">
        <div slot="header" class="info-card-header">
          <i class="el-icon-user info-card-icon"></i>
          <span>操作记录</span>
        </div>
        <el-descriptions :column="4" size="small" class="info-desc">
          <el-descriptions-item label="明细条数">{{ taskRow.itemCount != null ? taskRow.itemCount : (taskRow.items || []).length }}</el-descriptions-item>
          <el-descriptions-item label="发起人">{{ taskRow.applicantName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ displayTime(taskRow.submitTime) }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ displayTime(taskRow.finishTime) }}</el-descriptions-item>
        </el-descriptions>
      </el-card>
      <el-divider content-position="left">
        <i class="el-icon-tickets"></i>
        <span style="margin-left: 6px">调价明细（{{ (taskRow.items || []).length }} 条）</span>
      </el-divider>
      <el-table :data="taskRow.items || []" border size="small" max-height="320">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="merchantPhone" label="电话" width="120" />
        <el-table-column label="型号" min-width="120">
          <template slot-scope="scope">{{ scope.row.goodsFilterName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="originalPrice" label="原价(元/㎡)" width="110" align="center" />
        <el-table-column label="上次调价(元/㎡)" prop="latestAdjustedPrice" width="130" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.latestAdjustedPrice == null" style="color: #c0c4cc">-</span>
            <span v-else style="color: #909399">{{ Number(scope.row.latestAdjustedPrice).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="调整后(元/㎡)" width="120" align="center">
          <template slot-scope="scope">
            <span style="color: #f56c6c; font-weight: 600">{{ scope.row.adjustedPrice }}</span>
          </template>
        </el-table-column>
        <el-table-column label="有效期" min-width="230">
          <template slot-scope="scope">
            <span v-if="isCancelBatch">{{ displayTime(scope.row.effectiveStartTime) }} ~ {{ displayTime(scope.row.effectiveEndTime) }}</span>
            <span v-else style="color: #c0c4cc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="明细状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag
              v-if="scope.row.taskDisplayStatus"
              :type="displayStatusType(scope.row.taskDisplayStatus)"
              size="mini"
            >{{ scope.row.taskDisplayStatus }}</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <!-- 新增模式：选商户价 + 配置 + 预览 -->
    <template v-else>
      <!-- 1. 选商户价 -->
      <el-divider content-position="left">
        <i class="el-icon-shopping-cart-2"></i>
        <span style="margin-left: 6px">选择商户价</span>
      </el-divider>
      <div class="merchant-filter">
        <el-input
          v-model="merchantSearch"
          placeholder="输入商户名称筛选"
          size="small"
          clearable
          style="width: 220px"
          @keyup.enter.native="loadMerchantList"
          @clear="loadMerchantList"
        />
        <el-button size="small" icon="el-icon-search" @click="loadMerchantList">查询</el-button>
        <span class="selected-tip">
          已选 <b style="color: #f56c6c">{{ selectedMerchantIds.length }}</b> 条
        </span>
      </div>
      <el-table
        ref="merchantTable"
        :data="merchantList"
        border
        stripe
        size="small"
        max-height="240"
        @selection-change="handleMerchantSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="merchantPhone" label="电话" width="120" />
        <el-table-column label="扣板型号" min-width="120">
          <template slot-scope="scope">
            <template v-if="isCeiling">{{ scope.row.goodsFilterName || '-' }}</template>
          </template>
        </el-table-column>
        <el-table-column label="原价(元/㎡)" width="110" align="center">
          <template slot-scope="scope">{{ scope.row.price }}</template>
        </el-table-column>
        <el-table-column label="调价中" width="80" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.activeAdjustTask" type="danger" size="mini">是</el-tag>
            <span v-else style="color: #c0c4cc">-</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="merchant-pager">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :total="merchantPage.total"
          :current-page="merchantPage.currentPage"
          :page-size="merchantPage.pageSize"
          :page-sizes="[10, 20, 50]"
          @size-change="handleMerchantSizeChange"
          @current-change="handleMerchantPageChange"
        />
      </div>

      <!-- 2. 配置调价 -->
      <el-divider content-position="left">
        <i class="el-icon-set-up"></i>
        <span style="margin-left: 6px">配置调价</span>
      </el-divider>
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="100px"
        size="small"
      >
        <el-form-item label="调价方式" prop="adjustType">
          <el-radio-group v-model="form.adjustType" @change="handleTypeChange">
            <el-radio-button
              v-for="item in adjustTypeDic"
              :key="item.value"
              :label="item.value"
            >{{ item.label }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          v-if="form.adjustType === 'FIXED'"
          label="固定价"
          prop="adjustValue"
        >
          <el-input-number
            v-model="form.adjustValue"
            :min="0"
            :precision="2"
            :step="0.1"
            controls-position="right"
            style="width: 220px"
            placeholder="调整后单价（元/㎡）"
          />
          <span class="form-tip">单位：元/㎡</span>
        </el-form-item>

        <el-form-item
          v-if="form.adjustType === 'DISCOUNT'"
          label="折扣率"
          prop="adjustValue"
        >
          <el-input-number
            v-model="form.adjustValue"
            :min="0.01"
            :max="10"
            :precision="4"
            :step="0.01"
            controls-position="right"
            style="width: 220px"
            placeholder="如 0.93 表示 93 折"
          />
          <span class="form-tip">范围 (0, 10]，如 0.93 = 93 折</span>
        </el-form-item>

        <el-form-item
          v-if="form.adjustType === 'ADD'"
          label="加减金额"
          prop="adjustValue"
        >
          <el-input-number
            v-model="form.adjustValue"
            :precision="2"
            :step="0.1"
            controls-position="right"
            style="width: 220px"
            placeholder="可填负数，如 -2 表示减 2 元"
          />
          <span class="form-tip">单位：元/㎡，可负</span>
        </el-form-item>

        <el-form-item
          v-if="form.adjustType === 'FORMULA'"
          label="公式"
          prop="formula"
        >
          <el-input
            v-model="form.formula"
            placeholder="price 代表商户价，如 price*0.9+0.5"
            style="width: 380px"
          />
          <div class="form-tip">
            变量仅 price；支持 + - * / ( )；可用函数 round/floor/ceil/max/min/abs
          </div>
        </el-form-item>

        <el-form-item label="有效期" prop="effectiveRange">
          <el-date-picker
            v-model="form.effectiveRange"
            type="datetimerange"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss"
            range-separator="至"
            start-placeholder="生效时间"
            end-placeholder="失效时间"
            :picker-options="pickerOptions"
            style="width: 460px"
          />
          <div class="form-tip">失效时间必须晚于当前时间</div>
        </el-form-item>
      </el-form>

      <!-- 3. 预览 -->
      <template v-if="previewResult">
        <el-divider content-position="left">
          <i class="el-icon-view"></i>
          <span style="margin-left: 6px">预览结果</span>
        </el-divider>
        <div class="preview-result-box">
          <el-alert
            :title="`已根据当前参数预生成 ${(previewResult.items || []).length} 条调价记录，请确认无误后提交审批`"
            type="success"
            :closable="false"
            show-icon
          />
          <el-table :data="previewResult.items || []" border size="small" max-height="200" style="margin-top: 12px">
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="merchantPhone" label="电话" width="120" />
            <el-table-column label="原价(元/㎡)" prop="originalPrice" width="110" align="center" />
            <el-table-column label="上次调价(元/㎡)" prop="latestAdjustedPrice" width="130" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.latestAdjustedPrice == null" style="color: #c0c4cc">-</span>
                <span v-else style="color: #909399">{{ Number(scope.row.latestAdjustedPrice).toFixed(2) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="调整后(元/㎡)" prop="adjustedPrice" width="120" align="center">
              <template slot-scope="scope">
                <span style="color: #f56c6c; font-weight: 600">{{ scope.row.adjustedPrice }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </template>
    </template>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">关闭</el-button>
      <template v-if="!isView">
        <el-button
          type="primary"
          :loading="previewLoading"
          :disabled="!canPreview"
          @click="handlePreview"
        >预览</el-button>
        <el-button
          type="success"
          :loading="submitLoading"
          :disabled="!previewResult"
          @click="handleSubmit"
        >提交审批</el-button>
      </template>
    </span>
  </el-dialog>
</template>

<script>
import { formatterTime } from "@/util/util";
import { adjustTypeDic } from "@/const/work/waterProof/priceAdjustOption";
import { displayStatusTagType } from "@/const/work/waterProof/priceAdjustOption";
import { approvalStatusTextMap } from "@/const/work/waterProof/priceAdjustOption";
import { projectTypeDic } from "@/const/work/waterProof/merchantPriceOption";
import {
  previewPriceAdjust,
  submitPriceAdjust,
} from "@/api/work/waterProof/priceAdjust";
import { findPriceBookMerchant } from "@/api/work/waterProof/merchantPrice";
import Divider from "element-ui/lib/divider";
import Card from "element-ui/lib/card";
import Descriptions from "element-ui/lib/descriptions";
import DescriptionsItem from "element-ui/lib/descriptions-item";

export default {
  name: "PriceAdjustFormDialog",
  components: {
    "el-divider": Divider,
    "el-card": Card,
    "el-descriptions": Descriptions,
    "el-descriptions-item": DescriptionsItem,
  },
  props: {
    visible: { type: Boolean, default: false },
    mode: { type: String, default: "create" }, // create | view
    priceBook: { type: Object, default: null },
    taskRow: { type: Object, default: null },
  },
  data() {
    return {
      dialogVisible: false,
      isView: false,
      adjustTypeDic,
      // 选商户价
      merchantSearch: "",
      merchantList: [],
      merchantPage: { total: 0, currentPage: 1, pageSize: 10 },
      merchantLoading: false,
      selectedMerchantIds: [],
      // 调价配置
      form: this.initForm(),
      rules: {
        adjustType: [{ required: true, message: "请选择调价方式", trigger: "change" }],
        adjustValue: [{ required: true, message: "请输入调价参数", trigger: "blur" }],
        formula: [{ required: true, message: "请输入公式", trigger: "blur" }],
        effectiveRange: [{ required: true, message: "请选择有效期", trigger: "change" }],
      },
      pickerOptions: {
        disabledDate: (date) => date.getTime() < Date.now() - 24 * 60 * 60 * 1000,
      },
      previewLoading: false,
      submitLoading: false,
      previewResult: null,
    };
  },
  computed: {
    dialogTitle() {
      return this.isView ? "调价任务详情" : "新增调价";
    },
    // dialogKey：每次打开/模式/任务切换时改变，强制 dialog 整体重挂载，避免 element-ui 内部 renderCell
    // 闭包捕获的 $scopedSlots 在模式切换时残留导致 "$scopedSlots.default is not a function"
    dialogKey() {
      const mode = this.isView ? "view" : "create";
      const id = (this.taskRow && this.taskRow.id) || "new";
      const batchType = (this.taskRow && this.taskRow.batchType) || "";
      return `${mode}-${id}-${batchType}`;
    },
    // 作废批次（CANCEL）：无 adjustType/adjustValue/formula，行级有效期在 items 内
    isCancelBatch() {
      return !!(this.taskRow && this.taskRow.batchType === "CANCEL");
    },
    isCeiling() {
      return this.priceBook && this.priceBook.projectType === "HANG_CEILING";
    },
    // 预览/提交可用：选了方式 + 填了参数 + 选了有效期 + 至少 1 条商户价
    canPreview() {
      if (!this.form.adjustType || !this.selectedMerchantIds.length) return false;
      if (!this.form.effectiveRange || this.form.effectiveRange.length !== 2) return false;
      if (this.form.adjustType === "FORMULA") {
        return !!(this.form.formula && this.form.formula.trim());
      }
      return this.form.adjustValue !== null && this.form.adjustValue !== undefined && this.form.adjustValue !== "";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.isView = this.mode === "view";
          // 每次打开都重置可变状态，避免上次打开残留导致 element-ui renderCell 报错
          this.selectedMerchantIds = [];
          this.previewResult = null;
          this.previewLoading = false;
          this.submitLoading = false;
          if (this.isView) {
            // 查看模式无需加载商户列表
          } else {
            this.form = this.initForm();
            this.merchantSearch = "";
            this.merchantList = [];
            this.merchantPage = { total: 0, currentPage: 1, pageSize: 10 };
            this.$nextTick(() => {
              this.loadMerchantList();
            });
          }
        }
      },
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
  },
  methods: {
    formatTime: formatterTime,
    displayTime(v) {
      if (v == null || v === "") return "-";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      return this.formatTime(v);
    },
    parseData(res) {
      return typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
    },
    projectTypeText(t) {
      const item = projectTypeDic.find((d) => d.value === t);
      return item ? item.label : t || "";
    },
    displayStatusType(text) {
      return displayStatusTagType[text] || "info";
    },
    approvalStatusText(status) {
      return approvalStatusTextMap[status] || status || "-";
    },
    approvalStatusType(status) {
      if (status === "PENDING") return "warning";
      if (status === "REJECTED") return "danger";
      if (status === "APPROVED") return "success";
      if (status === "CANCELLED") return "info";
      return "info";
    },
    initForm() {
      return {
        adjustType: "DISCOUNT",
        adjustValue: null,
        formula: "",
        effectiveRange: [],
      };
    },
    // 加载商户价列表（内置分页 + 搜索；接口 page 从 0 开始，参考 MerchantPriceDrawer）
    loadMerchantList() {
      if (!this.priceBook) return;
      this.merchantLoading = true;
      findPriceBookMerchant({
        priceBookId: this.priceBook.id,
        merchantName: this.merchantSearch || undefined,
        page: this.merchantPage.currentPage - 1,
        size: this.merchantPage.pageSize,
      })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.merchantList = data.rows || [];
            this.merchantPage.total = data.total || 0;
          } else {
            this.$message.error(res.message || "商户价加载失败");
          }
        })
        .catch((err) => {
          console.log("loadMerchantList error:", err);
          this.$message.error("商户价加载异常");
        })
        .finally(() => {
          this.merchantLoading = false;
        });
    },
    handleMerchantSelectionChange(rows) {
      this.selectedMerchantIds = (rows || []).map((r) => r.id);
    },
    handleMerchantPageChange(page) {
      this.merchantPage.currentPage = page;
      this.loadMerchantList();
    },
    handleMerchantSizeChange(size) {
      this.merchantPage.pageSize = size;
      this.merchantPage.currentPage = 1;
      this.loadMerchantList();
    },
    handleTypeChange() {
      this.previewResult = null;
      this.form.adjustValue = null;
      this.form.formula = "";
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    buildPayload() {
      const payload = {
        priceBookId: this.priceBook.id,
        adjustType: this.form.adjustType,
        effectiveStartTime: this.form.effectiveRange[0],
        effectiveEndTime: this.form.effectiveRange[1],
        merchantPriceIds: [...this.selectedMerchantIds],
      };
      if (this.form.adjustType === "FORMULA") {
        payload.formula = this.form.formula;
        payload.adjustValue = null;
      } else {
        payload.adjustValue = this.form.adjustValue;
        payload.formula = null;
      }
      return payload;
    },
    handlePreview() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        this.previewLoading = true;
        previewPriceAdjust(this.buildPayload())
          .then((res) => {
            if (res.code === "200") {
              this.previewResult = typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
              this.$message.success("预览完成");
            } else {
              this.previewResult = null;
              this.$message.error(res.message || "预览失败");
            }
          })
          .catch((err) => {
            console.log("handlePreview error:", err);
            this.$message.error("预览异常");
          })
          .finally(() => {
            this.previewLoading = false;
          });
      });
    },
    handleSubmit() {
      if (!this.previewResult) {
        this.$message.warning("请先预览");
        return;
      }
      this.$confirm(
        "提交后将生成审批单，审批通过后生效。是否继续？",
        "提示",
        { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => {
          this.submitLoading = true;
          submitPriceAdjust(this.buildPayload())
            .then((res) => {
              if (res.code === "200") {
                const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
                this.$message.success(data.message || "已提交审批");
                this.$emit("submit-success");
                this.dialogVisible = false;
              } else {
                this.$message.error(res.message || "提交失败");
              }
            })
            .catch((err) => {
              console.log("handleSubmit error:", err);
              this.$message.error("提交异常");
            })
            .finally(() => {
              this.submitLoading = false;
            });
        })
        .catch(() => {});
    },
    handleClosed() {
      // 关闭后彻底重置所有可变状态，避免下次打开时残留
      this.isView = false;
      this.form = this.initForm();
      this.previewResult = null;
      this.previewLoading = false;
      this.submitLoading = false;
      this.selectedMerchantIds = [];
      this.merchantList = [];
      this.merchantSearch = "";
      this.merchantPage = { total: 0, currentPage: 1, pageSize: 10 };
      this.merchantLoading = false;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
  },
};
</script>

<style scoped>
.book-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ebeef5 100%);
  border-radius: 6px;
  border-left: 3px solid #409eff;
}
.book-info-icon {
  color: #409eff;
  font-size: 18px;
}
.book-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.merchant-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.selected-tip {
  margin-left: auto;
  font-size: 12px;
  color: #606266;
}
.merchant-pager {
  margin-top: 8px;
  text-align: right;
}
.form-tip {
  margin-left: 12px;
  color: #909399;
  font-size: 12px;
}
.preview-result-box {
  border: 1px solid #e1f3d8;
  border-radius: 6px;
  padding: 12px;
  background: #f0f9eb;
}
.info-card-row {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}
.info-card {
  flex: 1;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}
.info-card >>> .el-card__header {
  padding: 10px 16px;
  background: #fafbfc;
  border-bottom: 1px solid #ebeef5;
}
.info-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.info-card-icon {
  color: #409eff;
  font-size: 16px;
}
.info-card-full {
  margin-top: 12px;
}
.info-desc >>> .el-descriptions-item__label {
  width: 80px;
  color: #606266;
}
.info-desc >>> .el-descriptions-item__content {
  color: #303133;
}
</style>
